import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Input, Select, Typography } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import type { Contract } from "@/entity/purchasing/contract";
import { SORT } from "@/meta/common";
import { CONTRACT_STATE, HOW_TO_SIGN } from "@/meta/purchase/contract";
import { PAYMENT_TYPE } from "@/meta/purchase/payment";
import { dateFormat, getRealyState } from "@/utils";
import { useUserStore } from "@/views/stores";

export type ContractRow = Partial<Contract> & { id: number | string };
export type ContractListQuery = {
    page: number;
    size: number;
    param?: string;
    type?: number;
    signMode?: number;
    state?: number[];
    sortKey?: string;
    sortOrder?: string;
};
export type ContractListResult = { list?: ContractRow[]; total?: number };
export interface ContractPageProps {
    deleteContract?: (id: number | string) => Promise<unknown>;
    loadContracts?: (query: ContractListQuery) => Promise<ContractListResult>;
    permissions?: string[];
    revokeContract?: (id: number | string) => Promise<unknown>;
}

/** 复用 Vue 合同分页接口，路径由既有服务固定为 `/purchase/contract/paging`。 */
export async function loadPurchaseContracts(query: ContractListQuery): Promise<ContractListResult> {
    const { data } = await request.service.get<ContractListResult>("/purchase/contract/paging", { params: query });
    return data;
}

/** 复用 Vue 合同删除接口，草稿删除和线下关闭共用该契约。 */
export async function deletePurchaseContract(id: number | string) {
    const { data } = await request.service.delete(`/purchase/contract/${id}`);
    return data;
}

/** 复用 Vue 线上合同撤回接口。 */
export async function revokePurchaseContract(id: number | string) {
    const { data } = await request.service.put(`/purchase/contract/revoke/${id}`);
    return data;
}

// 复用旧 Vue useTable 的默认分页契约，每页默认请求 10 条合同。
const defaultQuery: ContractListQuery = { page: 1, size: 10 };
const enumLabel = (meta: { getIntroByCode: (code?: number | string) => string }, value?: number | string) =>
    value == null ? "--" : meta.getIntroByCode(Number(value)) || "--";
const enterpriseLabel = (enterprise?: { name?: string; realName?: string; mobile?: string }) =>
    enterprise?.name || enterprise?.realName || enterprise?.mobile || "--";

/** 兼容 Vue 合同接口的高位状态编码和测试中的低位状态编码。 */
const contractStateLabel = (value?: number | string) => {
    const realState = getRealyState(Number(value));
    return CONTRACT_STATE.getIntroByCode(realState) || enumLabel(CONTRACT_STATE, value);
};

/** 沿用旧 Vue 合同列表按真实状态和签署方式展示操作的规则。 */
export function isContractActionVisible(
    row: Pick<ContractRow, "state" | "signMode">,
    action: "修改" | "撤回" | "删除" | "关闭"
): boolean {
    const realState = getRealyState(Number(row.state));
    if (action === "修改" || action === "删除") return CONTRACT_STATE.DRAFT.match(realState);
    if (action === "撤回") {
        return CONTRACT_STATE.SIGNING.match(realState) && HOW_TO_SIGN.ONLINE.match(row.signMode);
    }
    return HOW_TO_SIGN.UNDERLINE.match(row.signMode);
}

/** 采购合同列表：保留 Vue 的筛选、分页、详情入口、空态和失败反馈。 */
export function ContractPage({
    deleteContract = deletePurchaseContract,
    loadContracts = loadPurchaseContracts,
    permissions,
    revokeContract = revokePurchaseContract
}: ContractPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions ?? storePermissions;
    const canManage = currentPermissions.includes("ROLE_PROJECT_DEAL");
    const [query, setQuery] = useState<ContractListQuery>(defaultQuery);
    const [param, setParam] = useState("");
    const [type, setType] = useState<number>();
    const [signMode, setSignMode] = useState<number>();
    const [state, setState] = useState<number[]>([]);
    const [rows, setRows] = useState<ContractRow[]>([]);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState("");

    /** 执行合同操作并刷新当前分页，保持 Vue 成功后的列表更新行为。 */
    const runContractAction = async (action: () => Promise<unknown>) => {
        try {
            await action();
            const result = await loadContracts(query);
            setRows(result.list || []);
            setTotal(result.total || 0);
            setError("");
        } catch {
            setError("采购合同操作失败");
        }
    };

    useEffect(() => {
        let active = true;
        void loadContracts(query)
            .then(result => {
                if (!active) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setError("");
            })
            .catch(() => {
                if (!active) return;
                setRows([]);
                setTotal(0);
                setError("采购合同加载失败");
            });
        return () => {
            active = false;
        };
    }, [loadContracts, query]);

    // 将 zhihao-ui-react 的排序事件转换为旧 Vue 合同接口参数。
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        const nextQuery = { ...query, page: 1 };
        delete nextQuery.sortKey;
        delete nextQuery.sortOrder;
        if (prop && order) {
            nextQuery.sortKey = prop;
            nextQuery.sortOrder = SORT.getIntroByCode(order);
        }
        setQuery(nextQuery);
    };

    // 用 zhihao-ui-react 列契约承接 Vue 合同列表字段、枚举、排序和详情入口。
    const columns: TableColumnProps<ContractRow>[] = [
        {
            key: "id",
            label: "编号",
            minWidth: 148,
            prop: "id",
            render: ({ row }) => <Link to={`/purchasingManage/contract/info/${row.id}`}>{String(row.id)}</Link>
        },
        { key: "name", label: "名称", minWidth: 176, prop: "name", render: ({ row }) => row.name || "--" },
        {
            key: "buyerEnterprise",
            label: "买方",
            minWidth: 176,
            prop: "buyerEnterprise",
            render: ({ row }) => enterpriseLabel(row.buyerEnterprise)
        },
        {
            key: "sellerEnterprise",
            label: "卖方",
            minWidth: 176,
            prop: "sellerEnterprise",
            render: ({ row }) => enterpriseLabel(row.sellerEnterprise)
        },
        {
            key: "type",
            label: "类型",
            minWidth: 176,
            prop: "type",
            render: ({ row }) => enumLabel(PAYMENT_TYPE, row.type)
        },
        {
            key: "signMode",
            label: "签署方式",
            minWidth: 106,
            prop: "signMode",
            render: ({ row }) => enumLabel(HOW_TO_SIGN, row.signMode)
        },
        {
            key: "handlerName",
            label: "项目专员",
            minWidth: 134,
            prop: "handlerName",
            render: ({ row }) => row.handlerName || "--"
        },
        {
            key: "updatedTime",
            label: "更新时间",
            width: 240,
            prop: "updatedTime",
            render: ({ row }) => (row.updatedTime ? dateFormat(row.updatedTime, DATETIME_PICKER_FORMAT) : "--")
        },
        {
            key: "state",
            label: "状态",
            minWidth: 106,
            prop: "state",
            render: ({ row }) => contractStateLabel(row.state)
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 176,
            prop: "operation",
            render: ({ row }) => {
                if (!canManage) return "--";
                const operationButton = (label: "修改" | "撤回" | "删除" | "关闭", onClick: () => void) => (
                    <Button aria-label={label} disabled={banButton} size="small" type="link" onClick={onClick}>
                        {label}
                    </Button>
                );
                return (
                    <div>
                        {isContractActionVisible(row, "修改")
                            ? operationButton(
                                  "修改",
                                  () => void navigate(`/purchasingManage/contract/add?id=${row.id}`)
                              )
                            : null}
                        {isContractActionVisible(row, "撤回")
                            ? operationButton("撤回", () => void runContractAction(() => revokeContract(row.id)))
                            : null}
                        {isContractActionVisible(row, "删除")
                            ? operationButton("删除", () => void runContractAction(() => deleteContract(row.id)))
                            : null}
                        {isContractActionVisible(row, "关闭")
                            ? operationButton("关闭", () => void runContractAction(() => deleteContract(row.id)))
                            : null}
                    </div>
                );
            }
        }
    ];

    const submitFilters = () =>
        setQuery({
            ...defaultQuery,
            ...(param.trim() ? { param: param.trim() } : {}),
            ...(type == null ? {} : { type }),
            ...(signMode == null ? {} : { signMode }),
            ...(state.length ? { state } : {})
        });

    return (
        <section aria-label="purchase-contract-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<ContractRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    rowKey={row => String(row.id)}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <div className="mb-4 flex items-center justify-between">
                                <Typography.Title className="mb-0 mt-0" level={4}>
                                    采购合同
                                </Typography.Title>
                                {canManage ? (
                                    <Link
                                        aria-disabled={banButton || undefined}
                                        to="/purchasingManage/contract/add"
                                        onClick={event => {
                                            if (banButton) event.preventDefault();
                                        }}
                                    >
                                        <Button disabled={banButton} type="primary">
                                            新增
                                        </Button>
                                    </Link>
                                ) : null}
                            </div>
                            {error ? <Alert className="mb-4" message={error} role="alert" type="error" /> : null}
                            <div className="admin-table-filter-form mb-4">
                                <Input
                                    aria-label="合同编号或名称"
                                    placeholder="请输入合同编号或合同名称"
                                    value={param}
                                    onChange={event => setParam(event.target.value)}
                                />
                                <Select
                                    aria-label="合同类型"
                                    allowClear
                                    options={PAYMENT_TYPE.options}
                                    placeholder="请选择合同类型"
                                    value={type}
                                    onChange={setType}
                                />
                                <Select
                                    aria-label="签署方式"
                                    allowClear
                                    options={HOW_TO_SIGN.options}
                                    placeholder="请选择签署方式"
                                    value={signMode}
                                    onChange={setSignMode}
                                />
                                <Select
                                    aria-label="合同状态"
                                    allowClear
                                    mode="multiple"
                                    options={CONTRACT_STATE.options}
                                    placeholder="请选择状态"
                                    value={state}
                                    onChange={value => setState(value)}
                                />
                                <Button type="primary" onClick={submitFilters}>
                                    查询
                                </Button>
                                <Button
                                    onClick={() => {
                                        setParam("");
                                        setType(undefined);
                                        setSignMode(undefined);
                                        setState([]);
                                        setQuery(defaultQuery);
                                    }}
                                >
                                    重置
                                </Button>
                            </div>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default ContractPage;
